import { Search } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../app/rootReducer';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { TaskTypes } from '../../tasks/types/taskTypes';
import { buildDisclosureAndDxCandidates } from './candidateMappers';
import { groupCandidatesByDoctor } from './groupCandidatesByDoctor';
import GroupedDoctorCard from './GroupedDoctorCard';
import { sortDoctorGroupsBySourceBuckets } from './sortDoctorGroups';
import { DisclosureDoctor, DxClaim } from './types';

const APS_MAX = 30;
const APS_COLLAPSED_COUNT = 7;
const APS_CANDIDATES_LOOKBACK_YEARS = 2;

interface ApsCandidatesProps {
  dxClaims: DxClaim[];
  disclosure: DisclosureDoctor[];
}

export default function ApsCandidates({
  dxClaims,
  disclosure,
}: ApsCandidatesProps) {
  const [expanded, setExpanded] = useState(false);

  const dxTaskCreatedAt = useSelector(
    (state: RootState) =>
      getAllTasks(state.taskList).find(
        t => t.taskType === TaskTypes.AUTO_MEDICAL_CLAIMS,
      )?.createdAt,
  );

  const filteredDxClaims = useMemo(() => {
    const anchor = dxTaskCreatedAt ? new Date(dxTaskCreatedAt) : new Date();
    anchor.setFullYear(anchor.getFullYear() - APS_CANDIDATES_LOOKBACK_YEARS);
    const cutoff = anchor.toISOString().slice(0, 10);
    return dxClaims.filter(c => c.visitDate && c.visitDate >= cutoff);
  }, [dxClaims, dxTaskCreatedAt]);

  const candidates = useMemo(
    () => buildDisclosureAndDxCandidates(disclosure, filteredDxClaims),
    [disclosure, filteredDxClaims],
  );

  // Group same (source, NPI, phone, address) into one doctor. Multiple Dx
  // claims for the same provider collapse into a single expandable card
  // carrying all ICD codes.
  const groups = useMemo(
    () => sortDoctorGroupsBySourceBuckets(groupCandidatesByDoctor(candidates)),
    [candidates],
  );

  if (groups.length === 0) {
    return (
      <div className="no-data-found-container">
        <Search />
        <p>No APS candidates</p>
      </div>
    );
  }

  const capped = groups.slice(0, APS_MAX);
  const visible = expanded ? capped : capped.slice(0, APS_COLLAPSED_COUNT);
  const hasMore = capped.length > APS_COLLAPSED_COUNT;

  return (
    <div className="aps-candidates">
      <div className="view-subheader">
        <span className="view-label">
          Showing <strong>{visible.length}</strong> of{' '}
          <strong>{capped.length} doctors</strong> with sufficient info for APS
        </span>
      </div>
      <div className={`aps-list ${expanded ? 'expanded' : ''}`}>
        {visible.map((group, idx) => (
          <GroupedDoctorCard key={group.key} group={group} rank={idx + 1} />
        ))}
        {expanded && hasMore && (
          <div className="aps-footer">
            <button
              type="button"
              className="aps-toggle-btn"
              onClick={() => setExpanded(false)}
            >
              Collapse
            </button>
          </div>
        )}
      </div>
      {!expanded && hasMore && (
        <div className="aps-footer">
          <button
            type="button"
            className="aps-toggle-btn"
            onClick={() => setExpanded(true)}
          >
            Expand ({capped.length - APS_COLLAPSED_COUNT} more)
          </button>
        </div>
      )}
    </div>
  );
}
